import { useEffect, useState } from 'react';
import { BottomActionBarV2, DialogV2, AritoIcon } from '@/components/custom/erp';
import { FormSchema, FormValues, initialFormValues } from '../schema';
import BasicInfoTab from './form-fields/BasicInfoTab';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  formMode: 'add' | 'edit' | 'view';
  initialData?: FormValues;
  onAddButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onCopyButtonClick?: () => void;
}

const FormDialog = ({
  open,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  formMode,
  initialData,
  onAddButtonClick,
  onEditButtonClick,
  onDeleteButtonClick,
  onCopyButtonClick
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});

  const normalizeFormValues = (data?: Partial<FormValues>): FormValues => ({
    ...initialFormValues,
    ...data,
    ma_pttt: data?.ma_pttt ?? '',
    ten_pttt: data?.ten_pttt ?? '',
    ten_pttt2: data?.ten_pttt2 ?? '',
    ma_ph_thuc_hddt: data?.ma_ph_thuc_hddt ?? '',
    status: String(data?.status ?? initialFormValues.status)
  });

  const handleValueChange = (field: keyof FormValues, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setIsFormDirty(true);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = FormSchema.safeParse(formValues);

    if (!result.success) {
      const nextErrors: Partial<Record<keyof FormValues, string>> = {};
      result.error.issues.forEach(issue => {
        const field = issue.path[0] as keyof FormValues | undefined;
        if (field) {
          nextErrors[field] = issue.message;
        }
      });
      setErrors(nextErrors);
      return;
    }

    onSubmit(result.data);
    setIsFormDirty(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      setFormValues(normalizeFormValues(initialData));
      setErrors({});
      setIsFormDirty(false);
    } else {
      setIsFormDirty(false);
    }
  }, [initialData, open]);

  return (
    <>
      <DialogV2
        open={open}
        onClose={onClose}
        title={`${formMode === 'add' ? 'Mới' : formMode === 'edit' ? 'Sửa' : 'Danh mục phương thức thanh toán'}`}
        maxWidth='xl'
        disableBackdropClose={false}
        disableEscapeKeyDown={true}
        titleIcon={<AritoIcon icon={281} />}
      >
        <form className='flex w-[min(800px,calc(100vw-64px))] flex-col' onSubmit={handleSubmit}>
          <div className='max-h-[calc(100vh-150px)] overflow-y-auto'>
            <BasicInfoTab formMode={formMode} values={formValues} errors={errors} onValueChange={handleValueChange} />
          </div>
          <div className='relative'>
            <>
              {formMode === 'view' && (
                <BottomActionBarV2
                  mode={formMode}
                  onSubmit={() => {}}
                  onClose={onClose}
                  onAdd={onAddButtonClick || onAdd}
                  onEdit={onEditButtonClick || onEdit}
                  onDelete={onDeleteButtonClick || onDelete}
                  onCopy={onCopyButtonClick || onCopy}
                />
              )}
              {formMode !== 'view' && <BottomActionBarV2 mode={formMode} onSubmit={() => {}} onClose={onClose} />}
            </>
          </div>
        </form>
      </DialogV2>
    </>
  );
};

export default FormDialog;
